import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../api/client'
import { useAuth } from './useAuth'
import { useCallback } from 'react'

export function useFavoriteAssets(org: string) {
    const { user } = useAuth()
    const queryClient = useQueryClient()
    const userUid = user?.uid ?? ''

    // The query key keeps the uid so the manager-facing `/users/{uid}` cache
    // invalidations still match; only the PATH moved to `me`. Reading or
    // writing your own favourites is gated on `organization.read`, not on
    // `contacts.read`, so a member whose only access comes through an
    // asset-scoped grant can star their own place (spec 2026-09-06-04).
    const queryKey = ['userSetting', org, userUid, 'favorite_assets']

    const { data: favorites = [], isLoading } = useQuery({
        queryKey,
        queryFn: async () => {
            const response = await apiClient.GET(
                '/api/v1/organizations/{orgCode}/users/me/settings/{settingCode}',
                {
                    params: {
                        path: { orgCode: org, settingCode: 'favorite_assets' },
                    },
                }
            )
            if (response.error) {
                // 404 means no favorites yet
                return [] as string[]
            }
            const data = response.data as any
            // The API returns { uid, value: [...], ... }
            const value = data?.value ?? data
            if (Array.isArray(value)) {
                return value as string[]
            }
            return [] as string[]
        },
        enabled: !!userUid && !!org,
        staleTime: 5 * 60 * 1000,
        retry: 1,
    })

    const mutation = useMutation({
        mutationFn: async (newFavorites: string[]) => {
            const response = await apiClient.PUT(
                '/api/v1/organizations/{orgCode}/users/me/settings/{settingCode}',
                {
                    params: {
                        path: { orgCode: org, settingCode: 'favorite_assets' },
                    },
                    body: newFavorites as any,
                }
            )
            if (response.error) {
                throw new Error('Failed to update favorites')
            }
        },
        onMutate: async (newFavorites: string[]) => {
            await queryClient.cancelQueries({ queryKey })
            const previous = queryClient.getQueryData<string[]>(queryKey)
            queryClient.setQueryData(queryKey, newFavorites)
            return { previous }
        },
        onError: (_err, _newFavorites, context) => {
            if (context?.previous !== undefined) {
                queryClient.setQueryData(queryKey, context.previous)
            }
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey })
        },
    })

    const isFavorite = useCallback(
        (assetUid: string) => favorites.includes(assetUid),
        [favorites]
    )

    const toggleFavorite = useCallback(
        (assetUid: string) => {
            const newFavorites = favorites.includes(assetUid)
                ? favorites.filter((uid) => uid !== assetUid)
                : [...favorites, assetUid]
            mutation.mutate(newFavorites)
        },
        [favorites, mutation]
    )

    return { favorites, isFavorite, toggleFavorite, isLoading }
}
